import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import Select from "react-select";

// Material UI Components & Icons
import { CircularProgress, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { apiURL } from "../../../../config/default";
import { showSnackBar } from "../../../../actions/showSnackBar";
import { searchProductSuggestions } from "../../../../services/products/product.service";
import DashboardPagesLayout from "../../customerServiceDashboard/components/DashboardPagesLayout";
import "./styles.css";

const renderContext = (context) => {
  if (!context) return null;
  if (typeof context === "string") {
    return context;
  }
  
  const {
    manufacturer,
    title,
    description,
    modelNumber,
    upc,
    categoryName,
    bullets,
    attributes
  } = context;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px", textAlign: "left" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
        {manufacturer && (
          <div>
            <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>MANUFACTURER</span>
            <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "500" }}>{manufacturer}</span>
          </div>
        )}
        {title && (
          <div>
            <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>TITLE</span>
            <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "500" }}>{title}</span>
          </div>
        )}
        {modelNumber && (
          <div>
            <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>MODEL NUMBER</span>
            <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "500" }}>{modelNumber}</span>
          </div>
        )}
        {upc && (
          <div>
            <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>UPC</span>
            <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "500" }}>{upc}</span>
          </div>
        )}
        {categoryName && (
          <div style={{ gridColumn: "span 2" }}>
            <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>CATEGORY</span>
            <span style={{ fontSize: "0.85rem", color: "#1e293b", fontWeight: "500" }}>{categoryName}</span>
          </div>
        )}
      </div>

      {description && (
        <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "8px" }}>
          <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600" }}>DESCRIPTION</span>
          <span style={{ fontSize: "0.8rem", color: "#475569", lineHeight: "1.4", display: "block" }}>{description}</span>
        </div>
      )}

      {Array.isArray(bullets) && bullets.length > 0 && (
        <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "8px" }}>
          <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600", marginBottom: "4px" }}>HIGHLIGHTS</span>
          <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: "0.8rem", color: "#475569" }}>
            {bullets.map((b, idx) => <li key={idx}>{b}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(attributes) && attributes.length > 0 && (
        <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "8px" }}>
          <span style={{ fontSize: "0.75rem", color: "#64748b", display: "block", fontWeight: "600", marginBottom: "6px" }}>ATTRIBUTES</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {attributes.map((attr, idx) => (
              <span 
                key={idx} 
                className="ai-keyword-tag" 
                style={{ 
                  margin: 0, 
                  fontSize: "0.75rem", 
                  backgroundColor: "#eff6ff", 
                  color: "#1e40af", 
                  borderColor: "#bfdbfe" 
                }}
              >
                {attr.name}: {attr.value}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const renderProductImage = (product) => {
  const imageUrl = product.mainImage || (product.images && product.images[0]);
  if (imageUrl) {
    return (
      <img 
        src={imageUrl} 
        alt={product.title} 
        style={{ 
          width: "40px", 
          height: "40px", 
          borderRadius: "8px", 
          objectFit: "cover",
          border: "1px solid #e2e8f0" 
        }} 
      />
    );
  }
  return (
    <div style={{
      width: "40px",
      height: "40px",
      borderRadius: "8px",
      backgroundColor: "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid #e2e8f0"
    }}>
      <AutoAwesomeIcon style={{ fontSize: "1.2rem", color: "#64748b" }} />
    </div>
  );
};

const AiProductTitles = () => {
  const dispatch = useDispatch();

  // Navigation Tabs: "dashboard" | "start" | "preview"
  const [activeTab, setActiveTab] = useState("dashboard");

  // Loading States
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [submittingJob, setSubmittingJob] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [autoPollActive, setAutoPollActive] = useState(false);
  const [resettingHealth, setResettingHealth] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [healthDiagnostics, setHealthDiagnostics] = useState(null);
  const [diagnosticsModalOpen, setDiagnosticsModalOpen] = useState(false);

  // Data States
  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    count: 0
  });
  
  // Cache of vendor list for selection
  const [vendors, setVendors] = useState([]);
  
  // Job Launch Form State
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);

  // Single Product Preview Form State
  const [previewProductId, setPreviewProductId] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Expanded Job Detail State
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // TAB 4: Optimized Products Tab State
  const [optimizedProducts, setOptimizedProducts] = useState([]);
  const [productsPagination, setProductsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    count: 0
  });
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [selectedProductVendor, setSelectedProductVendor] = useState(null);
  const [expandedProductId, setExpandedProductId] = useState(null);

  const handleProductRowClick = (productId) => {
    setExpandedProductId(prev => prev === productId ? null : productId);
  };

  // Polling reference
  const pollingIntervalRef = useRef(null);

  // Refs to prevent stale closure in polling interval and prevent infinite/unnecessary API calls on render
  const currentPageRef = useRef(pagination.currentPage);
  const expandedJobIdRef = useRef(expandedJobId);
  const jobsRef = useRef(jobs);
  const jobDetailRef = useRef(jobDetail);

  // Keep refs up-to-date
  useEffect(() => {
    currentPageRef.current = pagination.currentPage;
  }, [pagination.currentPage]);

  useEffect(() => {
    expandedJobIdRef.current = expandedJobId;
  }, [expandedJobId]);

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    jobDetailRef.current = jobDetail;
  }, [jobDetail]);

  // Fetch Vendor List (merged from stores & seo-optimize to ensure all IDs exist)
  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { "x-auth-token": token }
      };

      // Fetch from stores
      const storesPromise = axios.get(`${apiURL}admin/all-stores?page=1&limit=500`, config);
      // Fetch from seo-optimize
      const seoPromise = axios.get(`${apiURL}admin/seo-optimize?page=1&limit=500`, config);

      const [storesRes, seoRes] = await Promise.allSettled([storesPromise, seoPromise]);
      
      let mergedVendors = [];
      const seenIds = new Set();

      if (storesRes.status === "fulfilled" && storesRes.value.data) {
        const storeData = storesRes.value.data.data || storesRes.value.data;
        if (Array.isArray(storeData)) {
          storeData.forEach(item => {
            if (item && item._id && !seenIds.has(item._id)) {
              seenIds.add(item._id);
              mergedVendors.push({
                value: item._id,
                label: item.fullName || item.brandName || "Unknown Vendor"
              });
            }
          });
        }
      }

      if (seoRes.status === "fulfilled" && seoRes.value.data) {
        const seoData = seoRes.value.data.seoOptimizes || seoRes.value.data;
        if (Array.isArray(seoData)) {
          seoData.forEach(item => {
            const id = item.brandId || item._id;
            if (id && !seenIds.has(id)) {
              seenIds.add(id);
              mergedVendors.push({
                value: id,
                label: item.brandName || "Unknown Vendor"
              });
            }
          });
        }
      }

      // Sort alphabetically
      mergedVendors.sort((a, b) => a.label.localeCompare(b.label));
      setVendors(mergedVendors);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
      dispatch(showSnackBar("Error loading vendor lists", "error"));
    } finally {
      setLoadingVendors(false);
    }
  }, [dispatch]);

  // Fetch Optimized Products
  const fetchOptimizedProducts = useCallback(async (page = 1, searchQuery = "", vendorId = "") => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${apiURL}admin/ai-product-titles/products?page=${page}&limit=20`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (vendorId) {
        url += `&vendorId=${encodeURIComponent(vendorId)}&brand=${encodeURIComponent(vendorId)}`;
      }

      const res = await axios.get(url, { headers: { "x-auth-token": token } });

      if (res.data) {
        const productList = res.data.products || res.data.data || (Array.isArray(res.data) ? res.data : []);
        setOptimizedProducts(productList);

        const paginationInfo = res.data.pagination || {
          currentPage: page,
          totalPages: Math.ceil((res.data.count || productList.length) / 20) || 1,
          count: res.data.count || productList.length
        };
        setProductsPagination(paginationInfo);
      }
    } catch (error) {
      console.error("Failed to fetch optimized products:", error);
      dispatch(showSnackBar("Failed to load products list", "error"));
    } finally {
      setLoadingProducts(false);
    }
  }, [dispatch]);

  const handleClearFilters = () => {
    setProductSearchQuery("");
    setSelectedProductVendor(null);
    setProductsPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchOptimizedProducts(1, "", "");
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setProductsPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchOptimizedProducts(
        1,
        productSearchQuery,
        selectedProductVendor ? selectedProductVendor.value : ""
      );
    }
  };

  const handleVendorChange = (selectedOption) => {
    setSelectedProductVendor(selectedOption);
    setProductsPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Fetch Jobs List
  const fetchJobs = useCallback(async (page = 1) => {
    setLoadingJobs(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${apiURL}admin/ai-product-titles/jobs?page=${page}&limit=20`,
        { headers: { "x-auth-token": token } }
      );

      if (res.data) {
        const jobsList = res.data.jobs || res.data.data || (Array.isArray(res.data) ? res.data : []);
        setJobs(jobsList);
        
        // Update jobDetail from the new list if one is expanded
        if (expandedJobIdRef.current) {
          const updatedDetail = jobsList.find(j => (j.job?._id || j._id) === expandedJobIdRef.current);
          if (updatedDetail) {
            setJobDetail(prev => {
              if (prev) {
                const isPrevNested = prev.job !== undefined;
                const isNextNested = updatedDetail.job !== undefined;
                
                const nextObj = { ...prev };
                if (isPrevNested) {
                  nextObj.job = isNextNested ? updatedDetail.job : updatedDetail;
                } else {
                  Object.assign(nextObj, isNextNested ? updatedDetail.job : updatedDetail);
                }
                return nextObj;
              }
              return updatedDetail;
            });
          }
        }
        
        const paginationInfo = res.data.pagination || {
          currentPage: page,
          totalPages: Math.ceil((res.data.count || jobsList.length) / 20) || 1,
          count: res.data.count || jobsList.length
        };
        setPagination(paginationInfo);
      }
    } catch (error) {
      console.error("Failed to fetch AI jobs:", error);
      dispatch(showSnackBar("Failed to load jobs list", "error"));
    } finally {
      setLoadingJobs(false);
    }
  }, [dispatch]);

  // Fetch Single Job details (for expanded row)
  const fetchJobDetail = async (jobId) => {
    setLoadingDetail(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${apiURL}admin/ai-product-titles/jobs/${jobId}`,
        { headers: { "x-auth-token": token } }
      );
      if (res.data) {
        setJobDetail(res.data);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      dispatch(showSnackBar("Failed to load job details", "error"));
    } finally {
      setLoadingDetail(false);
    }
  };

  // Start Job
  const handleStartJob = async (e) => {
    e.preventDefault();
    if (!selectedVendor) {
      dispatch(showSnackBar("Please select a vendor/brand", "error"));
      return;
    }

    setSubmittingJob(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${apiURL}admin/ai-product-titles/start/${selectedVendor.value}`,
        { overwriteExisting, onlyActive },
        { headers: { "x-auth-token": token } }
      );

      if (res.status === 202) {
        dispatch(showSnackBar("AI Product Title optimization job successfully queued!", "success"));
        setSelectedVendor(null);
        setActiveTab("dashboard");
        fetchJobs(1);
      }
    } catch (error) {
      console.error("Failed to start AI job:", error);
      const errMsg = error.response?.data?.message || "Failed to queue job. A job might already be running for this vendor.";
      dispatch(showSnackBar(errMsg, "error"));
    } finally {
      setSubmittingJob(false);
    }
  };

  // Cancel Job
  const handleCancelJob = async (jobId, e) => {
    e.stopPropagation(); // Prevent row expansion
    if (!window.confirm("Are you sure you want to cancel this job?")) return;

    setCancellingJobId(jobId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiURL}admin/ai-product-titles/jobs/${jobId}/cancel`,
        {},
        { headers: { "x-auth-token": token } }
      );
      dispatch(showSnackBar("Job cancellation request submitted.", "success"));
      fetchJobs(pagination.currentPage);
      if (expandedJobId === jobId) {
        fetchJobDetail(jobId);
      }
    } catch (error) {
      console.error("Failed to cancel job:", error);
      dispatch(showSnackBar("Failed to cancel job", "error"));
    } finally {
      setCancellingJobId(null);
    }
  };

  // Reset Provider Health
  const handleResetHealth = async () => {
    if (!window.confirm("Are you sure you want to reset the AI Provider Health? This will clear any rate limits or circuit breakers.")) return;

    setResettingHealth(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${apiURL}admin/ai-product-titles/providers/reset-health`,
        {},
        { headers: { "x-auth-token": token } }
      );
      dispatch(showSnackBar(res.data?.message || "AI Provider health tracking has been successfully reset.", "success"));
      
      if (res.data?.diagnostics) {
        setHealthDiagnostics(res.data.diagnostics);
        setDiagnosticsModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to reset provider health:", error);
      dispatch(showSnackBar("Failed to reset AI Provider health", "error"));
    } finally {
      setResettingHealth(false);
    }
  };

  // Check Provider Status
  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${apiURL}admin/ai-product-titles/providers/status`,
        { headers: { "x-auth-token": token } }
      );
      
      if (res.data?.diagnostics) {
        setHealthDiagnostics(res.data.diagnostics);
        setDiagnosticsModalOpen(true);
      } else if (res.data) {
        setHealthDiagnostics(res.data);
        setDiagnosticsModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to fetch provider status:", error);
      dispatch(showSnackBar("Failed to fetch AI Provider status", "error"));
    } finally {
      setCheckingStatus(false);
    }
  };

  // Preview AI Title
  const handlePreview = async (e) => {
    e.preventDefault();
    if (!previewProductId.trim()) {
      dispatch(showSnackBar("Please enter a valid Product ID", "error"));
      return;
    }

    setGeneratingPreview(true);
    setPreviewResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${apiURL}admin/ai-product-titles/preview`,
        { productId: previewProductId.trim() },
        { headers: { "x-auth-token": token } }
      );
      console.log("res",res)
      if (res.data) {
        setPreviewResult(res.data);
        dispatch(showSnackBar("Preview title generated successfully!", "success"));
      }
    } catch (error) {
      console.error("Failed to generate preview:", error);
      const data = error?.data || error.response?.data || {};
      const message = (data.message || "").toLowerCase();
      const code = (data.code || "").toUpperCase();
      console.log("data",data)

      let funnyMsg = "";
      if (code === "GROQ_EMPTY_RESPONSE" || message.includes("groq returned an empty response") || message.includes("empty response")) {
        funnyMsg = "🤖 Groq is giving us the silent treatment! Empty response received. Go check if the server needs coffee.";
      } else if (message.includes("quota") || message.includes("insufficient") || message.includes("rate limit") || message.includes("limit exceeded") || message.includes("too many requests")) {
        funnyMsg = "💸 AI is on strike! Quota limit exceeded or credits are dry. Time to feed the coin slot or try again later!";
      } else {
        funnyMsg = data.message || "Failed to generate title preview. Verify if Product ID is correct.";
      }
      
      dispatch(showSnackBar(funnyMsg, "error"));
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Clean up search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleProductSearch = async (inputValue) => {
    setSearchingProducts(true);
    try {
      const res = await searchProductSuggestions({ title: inputValue });
      if (res && res.data && Array.isArray(res.data.products)) {
        const options = res.data.products.map(p => ({
          value: p._id,
          label: `${p.title} (Model: ${p.modelNumber || "N/A"} | SKU: ${p.sku || "N/A"})`
        }));
        setProductOptions(options);
      } else {
        setProductOptions([]);
      }
    } catch (err) {
      console.error("Failed to fetch product suggestions:", err);
      setProductOptions([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  const handleProductInputChange = (inputValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (inputValue.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        handleProductSearch(inputValue);
      }, 500);
    } else {
      setProductOptions([]);
    }
  };

  const handleProductSelect = (selectedOption) => {
    setSelectedProductOption(selectedOption);
    setPreviewProductId(selectedOption ? selectedOption.value : "");
  };

  // Copy product ID/title to clipboard helper
  const handleCopyToClipboard = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    dispatch(showSnackBar("Copied to clipboard!", "success"));
  };

  // Handle row click to expand details
  const handleRowClick = (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setJobDetail(null);
    } else {
      setExpandedJobId(jobId);
      const selectedJob = jobs.find(j => (j.job?._id || j._id) === jobId);
      setJobDetail(selectedJob || null);
    }
  };

  // Polling logic: Poll only if Auto-Sync is enabled, activeTab is dashboard, and a job is running
  const hasRunningJobs = jobs.some((job) => {
    const jobObj = job.job || job;
    return jobObj.status === "queued" || jobObj.status === "processing";
  });
  const shouldPoll = autoPollActive && activeTab === "dashboard" && hasRunningJobs;

  useEffect(() => {
    if (shouldPoll) {
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(() => {
          const token = localStorage.getItem("token");
          const currentPage = currentPageRef.current;
          const expandedId = expandedJobIdRef.current;

          // Silent refresh of jobs list
          axios.get(
            `${apiURL}admin/ai-product-titles/jobs?page=${currentPage}&limit=20`,
            { headers: { "x-auth-token": token } }
          ).then((res) => {
            if (res.data) {
              const jobsList = res.data.jobs || res.data.data || (Array.isArray(res.data) ? res.data : []);
              setJobs(jobsList);
              
              // If detail panel is open, check if it's still active. If so, refresh it too
              if (expandedId) {
                const currentDetail = jobDetailRef.current;
                const currentJobObj = currentDetail?.job || currentDetail;
                const isDetailActive = currentJobObj && (currentJobObj.status === "queued" || currentJobObj.status === "processing");
                if (isDetailActive) {
                  axios.get(
                    `${apiURL}admin/ai-product-titles/jobs/${expandedId}`,
                    { headers: { "x-auth-token": token } }
                  ).then((detailRes) => {
                    if (detailRes.data) {
                      setJobDetail(detailRes.data);
                    }
                  }).catch(err => console.warn("Detail polling error:", err));
                }
              }
            }
          }).catch(err => console.warn("Polling error:", err));
        }, 5000); // Poll every 5 seconds to reduce rate
      }
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [shouldPoll]);

  // Initial Fetches
  useEffect(() => {
    fetchJobs(1);
    fetchVendors();
  }, [fetchJobs, fetchVendors]);

  useEffect(() => {
    if (activeTab === "products") {
      fetchOptimizedProducts(
        productsPagination.currentPage,
        productSearchQuery,
        selectedProductVendor ? selectedProductVendor.value : ""
      );
    }
  }, [activeTab, productsPagination.currentPage, selectedProductVendor]);

  // Map Vendor ID to display Name helper
  const getVendorName = (vendorId) => {
    const found = vendors.find(v => v.value === vendorId);
    return found ? found.label : vendorId;
  };

  // Render details for an expanded job run
  const renderJobDetail = (detail) => {
    if (!detail) return null;
    const jobObj = detail.job || detail;
    const totalProducts = jobObj.totalCount ?? 0;
    const successes = jobObj.successCount ?? 0;
    const failuresCount = jobObj.failedCount ?? 0;
    const skipped = jobObj.skippedCount ?? 0;
    const processed = jobObj.processedCount ?? (successes + failuresCount + skipped);
    
    const status = jobObj.status;
    let pending = detail.pendingCount;
    if (pending === undefined || pending === null) {
      if (status === "cancelled" || status === "completed" || status === "failed") {
        pending = 0;
      } else {
        pending = Math.max(0, totalProducts - processed);
      }
    }
    
    const failuresList = detail.recentFailures || detail.failures || [];
    
    return (
      <>
        <div className="ai-job-counters">
          <div className="ai-counter-box">
            <div className="ai-counter-val">{totalProducts}</div>
            <div className="ai-counter-lbl">Total Products</div>
          </div>
          <div className="ai-counter-box" style={{ borderLeft: "4px solid #10b981" }}>
            <div className="ai-counter-val text-success">{successes}</div>
            <div className="ai-counter-lbl">Successes</div>
          </div>
          <div className="ai-counter-box" style={{ borderLeft: "4px solid #ef4444" }}>
            <div className="ai-counter-val text-danger">{failuresCount}</div>
            <div className="ai-counter-lbl">Failures</div>
          </div>
          {skipped > 0 && (
            <div className="ai-counter-box" style={{ borderLeft: "4px solid #ca8a04" }}>
              <div className="ai-counter-val text-warning">{skipped}</div>
              <div className="ai-counter-lbl">Skipped</div>
            </div>
          )}
          <div className="ai-counter-box" style={{ borderLeft: "4px solid #64748b" }}>
            <div className="ai-counter-val">{pending}</div>
            <div className="ai-counter-lbl">Pending</div>
          </div>
        </div>

        {/* Error Failures Log list (API returns up to 20 recent failures) */}
        {failuresList.length > 0 ? (
          <div>
            <h6 className="font-weight-bold text-danger d-flex align-items-center gap-1 mb-2">
              <ErrorIcon fontSize="small" /> 
              Recent Executions Failures ({failuresList.length})
            </h6>
            <div className="ai-failure-list">
              {failuresList.map((fail, fIdx) => (
                <div key={fIdx} className="ai-failure-item">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-weight-bold text-dark">
                      Product ID: <code className="c-pointer" onClick={(e) => handleCopyToClipboard(fail.productId, e)}>{fail.productId}</code>
                    </span>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {fail.timestamp ? moment(fail.timestamp).format("hh:mm:ss A") : ""}
                    </span>
                  </div>
                  <div className="text-danger font-weight-500">
                    Reason: {fail.error || "Unknown AI execution error"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-success font-weight-bold d-flex align-items-center gap-1 mt-2">
            <CheckCircleIcon fontSize="small" />
            No failures recorded for this execution run.
          </div>
        )}
      </>
    );
  };

  // Stats calculation
  const totalJobsCount = pagination.count || jobs.length;
  const runningJobsCount = jobs.filter(j => (j.job?.status || j.status) === "processing").length;
  const queuedJobsCount = jobs.filter(j => (j.job?.status || j.status) === "queued").length;
  const completedJobsCount = jobs.filter(j => (j.job?.status || j.status) === "completed").length;

  return (
    <div className="ai-dashboard-container">
      {/* Quick Dashboard Counters */}
      <div className="ai-stats-row">
        <div className="ai-stat-card ai-glass-card">
          <div className="ai-stat-icon completed">
            <CheckCircleIcon />
          </div>
          <div>
            <div className="ai-stat-value">{completedJobsCount}</div>
            <div className="ai-stat-label">Completed</div>
          </div>
        </div>

        <div className="ai-stat-card ai-glass-card">
          <div className="ai-stat-icon processing">
            {runningJobsCount > 0 ? (
              <CircularProgress size={20} thickness={5} className="ai-pulse" style={{ color: "#2563eb" }} />
            ) : (
              <PlayArrowIcon style={{ color: "#2563eb" }} />
            )}
          </div>
          <div>
            <div className="ai-stat-value">{runningJobsCount}</div>
            <div className="ai-stat-label">Running</div>
          </div>
        </div>

        <div className="ai-stat-card ai-glass-card">
          <div className="ai-stat-icon queued">
            <InfoIcon />
          </div>
          <div>
            <div className="ai-stat-value">{queuedJobsCount}</div>
            <div className="ai-stat-label">Queued</div>
          </div>
        </div>

        <div className="ai-stat-card ai-glass-card">
          <div className="ai-stat-icon failed">
            <ErrorIcon />
          </div>
          <div>
            <div className="ai-stat-value">{totalJobsCount}</div>
            <div className="ai-stat-label">Total Jobs</div>
          </div>
        </div>
      </div>

      {/* Dashboard Sub Navigation Tabs */}
      <div className="ai-nav-tabs">
        <button 
          id="ai-tab-dashboard"
          className={`ai-nav-tab-btn ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => setActiveTab("dashboard")}
        >
          Jobs History Dashboard
        </button>
        <button 
          id="ai-tab-products"
          className={`ai-nav-tab-btn ${activeTab === "products" ? "active" : ""}`}
          onClick={() => setActiveTab("products")}
        >
          AI Optimized Products
        </button>
        <button 
          id="ai-tab-start"
          className={`ai-nav-tab-btn ${activeTab === "start" ? "active" : ""}`}
          onClick={() => setActiveTab("start")}
        >
          Queue Bulk AI Optimization
        </button>
        <button 
          id="ai-tab-preview"
          className={`ai-nav-tab-btn ${activeTab === "preview" ? "active" : ""}`}
          onClick={() => setActiveTab("preview")}
        >
          Single Product Preview Tool
        </button>
      </div>

      {/* TAB 1: HISTORY DASHBOARD */}
      {activeTab === "dashboard" && (
        <DashboardPagesLayout
          pageTitle="Job Launch History"
          subTitle="runs recorded"
          count={totalJobsCount}
          rightHeaderContent={
            <div className="d-flex align-items-center gap-2">
              <button
                className="ai-btn-outline d-flex align-items-center gap-1"
                style={{
                  borderColor: autoPollActive ? "#10b981" : "#cbd5e0",
                  color: autoPollActive ? "#10b981" : "#64748b",
                  backgroundColor: autoPollActive ? "#ecfdf5" : "transparent"
                }}
                onClick={() => setAutoPollActive(!autoPollActive)}
                type="button"
              >
                <span style={{ fontSize: "14px", marginRight: "4px" }}>
                  {autoPollActive ? "●" : "○"}
                </span>
                {autoPollActive ? "Auto-Sync ON" : "Auto-Sync OFF"}
              </button>
              <button 
                className="ai-btn-outline d-flex align-items-center gap-2"
                onClick={handleCheckStatus}
                disabled={checkingStatus}
                type="button"
                style={{ borderColor: "#3b82f6", color: "#3b82f6" }}
              >
                {checkingStatus ? <CircularProgress size={16} color="inherit" /> : <InfoIcon fontSize="small" />}
                Check Status
              </button>
              <button 
                className="ai-btn-outline d-flex align-items-center gap-2"
                onClick={handleResetHealth}
                disabled={resettingHealth}
                type="button"
                style={{ borderColor: "#ef4444", color: "#ef4444" }}
              >
                {resettingHealth ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
                Reset Health
              </button>
              <button 
                className="ai-btn-outline d-flex align-items-center gap-2"
                onClick={() => {
                  fetchJobs(pagination.currentPage);
                  if (expandedJobId) {
                    fetchJobDetail(expandedJobId);
                  }
                }}
                disabled={loadingJobs}
                type="button"
              >
                <RefreshIcon className={loadingJobs || loadingDetail ? "rotate-animation" : ""} />
                Refresh
              </button>
            </div>
          }
        >
          <div className="ai-table-container">
            <table className="ai-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Vendor / Brand</th>
                  <th>Status</th>
                  <th>Provider / Model</th>
                  <th>Progress / Success</th>
                  <th>Launched At</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingJobs && jobs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <CircularProgress size={30} />
                      <div className="mt-2 text-muted font-weight-bold">Loading executions...</div>
                    </td>
                  </tr>
                )}

                {jobs.length === 0 && !loadingJobs && (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      No AI jobs history found. Queue a bulk job to optimize titles.
                    </td>
                  </tr>
                )}

                {jobs.map((job) => {
                  const jobObj = job.job || job;
                  const jobId = jobObj._id || job._id;
                  const isExpanded = expandedJobId === jobId;
                  const total = jobObj.totalCount || 0;
                  const success = jobObj.successCount || 0;
                  const failed = jobObj.failedCount || 0;
                  const skipped = jobObj.skippedCount || 0;
                  const processed = jobObj.processedCount ?? (success + failed + skipped);
                  const successRate = total > 0 ? Math.round((success / total) * 100) : 0;
                  const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;

                  return (
                    <React.Fragment key={jobId}>
                      <tr 
                        className="c-pointer" 
                        onClick={() => handleRowClick(jobId)}
                        style={{ background: isExpanded ? "#f8fafc" : "transparent" }}
                      >
                        <td className="font-weight-bold">
                          <span className="d-flex align-items-center gap-1">
                            {jobId.substring(jobId.length - 8)}
                            <ContentCopyIcon 
                              fontSize="inherit" 
                              className="text-muted copyIcon"
                              onClick={(e) => handleCopyToClipboard(jobId, e)} 
                            />
                          </span>
                        </td>
                        <td className="font-weight-600">
                          {getVendorName(jobObj.vendorId)}
                        </td>
                        <td>
                          <span className={`ai-badge ${jobObj.status}`}>
                            {jobObj.status === "processing" && (
                              <CircularProgress size={10} thickness={6} style={{ color: "#1e40af", marginRight: "2px" }} />
                            )}
                            {jobObj.status}
                          </span>
                        </td>
                        <td>
                          <div className="font-weight-bold text-dark">{jobObj.provider || "Gemini"}</div>
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>{jobObj.model || "gemini-2.5-flash-lite"}</div>
                        </td>
                        <td>
                          <div className="ai-progress-wrapper">
                            <div className="ai-progress-track">
                              <div className="ai-progress-bar" style={{ width: `${progressPct}%` }}></div>
                            </div>
                            <span className="ai-progress-text">{processed}/{total} ({successRate}%)</span>
                          </div>
                        </td>
                        <td className="text-muted">
                          {moment(jobObj.createdAt).format("MMM DD, hh:mm A")}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button 
                              className="ai-btn-outline" 
                              style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                              onClick={() => handleRowClick(jobId)}
                            >
                              <VisibilityIcon fontSize="inherit" style={{ marginRight: "4px" }} />
                              Detail
                            </button>
                            {(jobObj.status === "processing" || jobObj.status === "queued") && (
                              <button 
                                className="btn btn-danger d-flex align-items-center justify-content-center"
                                style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                                onClick={(e) => handleCancelJob(jobId, e)}
                                disabled={cancellingJobId === jobId}
                              >
                                {cancellingJobId === jobId ? <CircularProgress size={12} style={{ color: "white" }} /> : <CancelIcon fontSize="inherit" style={{ marginRight: "4px" }} />}
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Job Detail Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0, borderTop: "none" }}>
                            <div className="ai-job-detail-panel">
                              {loadingDetail && !jobDetail && (
                                <div className="text-center py-4">
                                  <CircularProgress size={24} />
                                  <div className="mt-1 text-muted">Loading run status details...</div>
                                </div>
                              )}

                              {jobDetail && renderJobDetail(jobDetail)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center align-center p-3">
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={(e, val) => fetchJobs(val)}
              />
            </div>
          )}
        </DashboardPagesLayout>
      )}

      {/* TAB 2: QUEUE BULK GENERATION */}
      {activeTab === "start" && (
        <div className="ai-glass-card ai-form-card">
          <h4 className="font-weight-bold mb-3 d-flex align-items-center gap-2">
            <PlayArrowIcon style={{ color: "#2563eb" }} />
            Start Bulk AI Titles Generation Job
          </h4>
          <p className="text-muted mb-4">
            Select a vendor to run bulk AI generation for all of their products. This operation gets queued in the background and runs asynchronously to prevent server timeouts.
          </p>

          {hasRunningJobs && (
            <div className="ai-alert-banner warning mb-4" style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px 16px",
              borderRadius: "8px",
              backgroundColor: "#fffbeb",
              borderLeft: "4px solid #f59e0b",
              color: "#b45309",
              fontSize: "0.9rem"
            }}>
              <InfoIcon style={{ color: "#d97706" }} />
              <div>
                <strong>Active Job In Progress:</strong> An optimization job is currently running or queued. You can still start other optimization jobs for different vendors/brands.
              </div>
            </div>
          )}

          <form onSubmit={handleStartJob} className="row">
            <div className="col-12 col-md-8 mb-4">
              <label className="ai-label">Select Vendor / Brand</label>
              {loadingVendors ? (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <CircularProgress size={16} />
                  <span className="text-muted">Loading brand registries...</span>
                </div>
              ) : (
                <Select
                  options={vendors}
                  value={selectedVendor}
                  onChange={setSelectedVendor}
                  placeholder="Type or select a vendor brand..."
                  isClearable
                  isLoading={loadingVendors}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "10px",
                      borderColor: "#cbd5e0",
                      padding: "3px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e0" }
                    })
                  }}
                />
              )}
            </div>

            <div className="col-12 col-md-8 mb-4">
              <div className="ai-option-item">
                <div>
                  <div className="font-weight-bold text-dark">Overwrite Existing AI Titles</div>
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    Regenerate and replace the product title even if an optimized AI title already exists in the database.
                  </div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    className="form-check-input CheckColor"
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>

              <div className="ai-option-item">
                <div>
                  <div className="font-weight-bold text-dark">Only Active Products</div>
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    Only optimize products that are currently active (status: true). Recommended for optimization efficiency.
                  </div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={onlyActive}
                    onChange={(e) => setOnlyActive(e.target.checked)}
                    className="form-check-input CheckColor"
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-md-8">
              <button 
                type="submit" 
                className="ai-btn-primary" 
                disabled={submittingJob || !selectedVendor}
              >
                {submittingJob ? (
                  <>
                    <CircularProgress size={18} style={{ color: "white" }} />
                    Queueing Job...
                  </>
                ) : (
                  <>
                    <AutoAwesomeIcon />
                    Start Background Job
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TAB 3: SINGLE PRODUCT PREVIEW TOOL */}
      {activeTab === "preview" && (
        <div className="ai-glass-card ai-form-card">
          <h4 className="font-weight-bold mb-3 d-flex align-items-center gap-2">
            <AutoAwesomeIcon style={{ color: "#2563eb" }} />
            Single Product AI Title Preview
          </h4>
          <p className="text-muted mb-4">
            Test AI title generation for a single product. This queries the AI models in real-time and displays the comparison without saving anything to the database, allowing you to fine-tune and audit the output quality.
          </p>

          <div className="row mb-4">
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <label className="ai-label">Search and Select Product</label>
              <Select
                options={productOptions}
                value={selectedProductOption}
                onInputChange={handleProductInputChange}
                onChange={handleProductSelect}
                placeholder="Type 3+ characters to search products..."
                isClearable
                isLoading={searchingProducts}
                noOptionsMessage={({ inputValue }) => 
                  inputValue.length < 3 ? "Type 3 or more characters to search" : "No products found"
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "10px",
                    borderColor: "#cbd5e0",
                    padding: "3px",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#cbd5e0" }
                  })
                }}
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="ai-label">Product ID (Auto-filled or paste manually)</label>
              <input
                type="text"
                className="ai-input"
                placeholder="MongoDB Product ID (e.g. 60fb4d5e2a229a1744b1c2b5)"
                value={previewProductId}
                onChange={(e) => {
                  setPreviewProductId(e.target.value);
                  setSelectedProductOption(null); // Clear selected dropdown if typed manually
                }}
                style={{ height: "46px" }}
              />
            </div>
          </div>

          <form onSubmit={handlePreview} className="d-flex justify-content-end mb-4">
            <button 
              type="submit" 
              className="ai-btn-primary" 
              style={{ height: "48px" }}
              disabled={generatingPreview || !previewProductId.trim()}
            >
              {generatingPreview ? (
                <>
                  <CircularProgress size={16} style={{ color: "white" }} />
                  Generating AI Draft...
                </>
              ) : (
                <>
                  <VisibilityIcon />
                  Generate Live Preview
                </>
              )}
            </button>
          </form>

          {previewResult && (
            <div>
              <hr className="my-4" />
              <h5 className="font-weight-bold text-dark mb-3">AI Optimization Audit Result</h5>
              
              <div className="ai-preview-comparison">
                {/* OLD / CURRENT CARD */}
                <div className="ai-comparison-card old">
                  <h6 className="font-weight-bold text-danger mb-3">Current Details</h6>
                  
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: "0.8rem" }}>Original Product Title</label>
                    <div className="font-weight-bold" style={{ fontSize: "1.1rem" }}>{previewResult.currentTitle || "N/A"}</div>
                  </div>

                  <div>
                    <label className="text-muted" style={{ fontSize: "0.8rem" }}>Existing AI Title in DB</label>
                    <div className="text-secondary font-italic">{previewResult.currentAiTitle || "None Saved"}</div>
                  </div>
                </div>

                {/* NEW / GENERATED CARD */}
                <div className="ai-comparison-card new">
                  <h6 className="font-weight-bold text-success mb-3">AI Optimized Recommendation</h6>
                  
                  <div className="mb-3">
                    <label className="text-muted" style={{ fontSize: "0.8rem" }}>Draft AI Product Title</label>
                    <div className="font-weight-bold text-primary" style={{ fontSize: "1.15rem" }}>{previewResult.aiTitle || "N/A"}</div>
                  </div>

                  {previewResult.keywords && previewResult.keywords.length > 0 && (
                    <div className="mb-3">
                      <label className="text-muted" style={{ fontSize: "0.8rem", display: "block" }}>Extracted Keywords</label>
                      <div style={{ marginTop: "4px" }}>
                        {previewResult.keywords.map((kw, idx) => (
                          <span key={idx} className="ai-keyword-tag">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="ai-meta-grid">
                    <div className="ai-meta-item">
                      <div className="ai-meta-lbl">AI Provider</div>
                      <div className="ai-meta-val text-capitalize">{previewResult.provider || "gemini"}</div>
                    </div>
                    
                    <div className="ai-meta-item">
                      <div className="ai-meta-lbl">Model Used</div>
                      <div className="ai-meta-val">{previewResult.model || "gemini-2.5-flash-lite"}</div>
                    </div>

                    {previewResult.context && (
                      <div className="ai-meta-item" style={{ gridColumn: "span 2" }}>
                        <div className="ai-meta-lbl">Context Scope</div>
                        <div className="ai-meta-val" style={{ fontWeight: "normal", fontSize: "0.8rem", color: "#475569" }}>
                          {renderContext(previewResult.context)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: AI OPTIMIZED PRODUCTS */}
      {activeTab === "products" && (
        <DashboardPagesLayout
          pageTitle="AI Optimized Products Registry"
          subTitle="products found"
          count={productsPagination.count}
          rightHeaderContent={
            <button 
              id="ai-product-refresh-btn"
              className="ai-btn-outline d-flex align-items-center gap-2"
              onClick={() => fetchOptimizedProducts(
                productsPagination.currentPage,
                productSearchQuery,
                selectedProductVendor ? selectedProductVendor.value : ""
              )}
              disabled={loadingProducts}
              type="button"
            >
              <RefreshIcon className={loadingProducts ? "rotate-animation" : ""} />
              Refresh
            </button>
          }
        >
          {/* Filters Bar */}
          <div className="ai-glass-card p-4 mb-4" style={{ background: "white" }}>
            <div className="row align-items-end">
              <div className="col-12 col-md-5 mb-3 mb-md-0">
                <label className="ai-label" htmlFor="ai-product-search-input">Search Products</label>
                <input
                  id="ai-product-search-input"
                  type="text"
                  className="ai-input"
                  placeholder="Search by Title, SKU, or Model..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ height: "46px" }}
                />
              </div>

              <div className="col-12 col-md-4 mb-3 mb-md-0">
                <label className="ai-label">Filter by Vendor / Brand</label>
                <Select
                  id="ai-product-vendor-select"
                  options={vendors}
                  value={selectedProductVendor}
                  onChange={handleVendorChange}
                  placeholder="All Vendors / Brands..."
                  isClearable
                  isLoading={loadingVendors}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "10px",
                      borderColor: "#cbd5e0",
                      padding: "3px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e0" }
                    })
                  }}
                />
              </div>

              <div className="col-12 col-md-3 d-flex gap-2">
                <button
                  id="ai-product-apply-btn"
                  className="ai-btn-primary flex-grow-1"
                  style={{ height: "46px", justifyContent: "center" }}
                  onClick={() => {
                    setProductsPagination(prev => ({ ...prev, currentPage: 1 }));
                    fetchOptimizedProducts(
                      1,
                      productSearchQuery,
                      selectedProductVendor ? selectedProductVendor.value : ""
                    );
                  }}
                >
                  Apply
                </button>
                <button
                  id="ai-product-clear-btn"
                  className="ai-btn-outline"
                  style={{ height: "46px", padding: "0 1.25rem" }}
                  onClick={handleClearFilters}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Table list */}
          <div className="ai-table-container">
            <table className="ai-table">
              <thead>
                <tr>
                  <th>Product Details</th>
                  <th>Original Product Title</th>
                  <th>AI Optimized Title</th>
                  <th>Provider / Model</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingProducts && optimizedProducts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      <CircularProgress size={30} />
                      <div className="mt-2 text-muted font-weight-bold">Loading optimized products...</div>
                    </td>
                  </tr>
                )}

                {optimizedProducts.length === 0 && !loadingProducts && (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      No products found matching the search criteria.
                    </td>
                  </tr>
                )}

                {optimizedProducts.map((product) => {
                  const vendorName = product.vendor?.fullName || product.manufacturer || getVendorName(product.brand) || getVendorName(product.vendorId) || "Unknown Vendor";
                  const isExpanded = expandedProductId === product._id;
                  return (
                    <React.Fragment key={product._id}>
                      <tr 
                        onClick={() => handleProductRowClick(product._id)}
                        className={isExpanded ? "bg-light" : ""}
                        style={{ cursor: "pointer", transition: "background-color 0.15s ease" }}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ color: "#94a3b8", display: "flex", alignItems: "center" }}>
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </div>
                            {renderProductImage(product)}
                            <div>
                              <div className="font-weight-bold text-dark" style={{ fontSize: "0.9rem" }}>
                                {product.modelNumber || "No Model"} {product.sku ? `(SKU: ${product.sku})` : ""}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                                ID: {product._id.substring(product._id.length - 8)}
                                <ContentCopyIcon 
                                  fontSize="inherit" 
                                  className="text-muted copyIcon ms-1"
                                  onClick={(e) => handleCopyToClipboard(product._id, e)} 
                                  style={{ cursor: "pointer" }}
                                />
                              </div>
                              <div className="text-primary font-weight-500" style={{ fontSize: "0.75rem" }}>
                                {vendorName}
                              </div>
                              <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                <span className={`ai-badge ${product.status ? 'completed' : 'cancelled'}`} style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem" }}>
                                  {product.status ? 'Active' : 'Inactive'}
                                </span>
                                {product.categoryName && (
                                  <span className="ai-keyword-tag" style={{ margin: 0, fontSize: "0.65rem", padding: "0.1rem 0.4rem", whiteSpace: "nowrap" }}>
                                    {product.categoryName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ maxWidth: "250px" }}>
                          {product.slug ? (
                            <a
                              href={
                                window.location.hostname.includes("qa") || window.location.hostname.includes("localhost")
                                  ? `https://qasundial.sundialhome.com/product/${product.slug}`
                                  : `https://www.sundialhome.com/product/${product.slug}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ textDecoration: "none" }}
                            >
                              <div 
                                title={product.title}
                                className="ai-product-title-link"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "normal",
                                  fontSize: "0.9rem",
                                  color: "#2563eb",
                                  fontWeight: "600",
                                  cursor: "pointer"
                                }}
                              >
                                {product.title || "N/A"}
                              </div>
                            </a>
                          ) : (
                            <div 
                              title={product.title}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                fontSize: "0.9rem"
                              }}
                            >
                              {product.title || "N/A"}
                            </div>
                          )}
                        </td>
                        <td style={{ maxWidth: "300px" }}>
                          {product.aiTitle ? (
                            <div 
                              className="text-success font-weight-600" 
                              title={product.aiTitle}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                fontSize: "0.9rem"
                              }}
                            >
                              {product.aiTitle}
                            </div>
                          ) : product.aiTitleError ? (
                            <div className="text-danger font-weight-500" style={{ fontSize: "0.85rem" }} title={product.aiTitleError}>
                              Failed: {product.aiTitleError}
                            </div>
                          ) : (
                            <span className="ai-badge failed" style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem" }}>
                              Not Optimized
                            </span>
                          )}
                        </td>
                        <td>
                          {product.aiTitleStatus === "success" || product.aiTitle ? (
                            <div>
                              <div className="font-weight-bold text-dark" style={{ fontSize: "0.8rem" }}>
                                {product.provider || "Gemini"}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                {product.model || "gemini-2.5-flash-lite"}
                              </div>
                              {product.aiTitleGeneratedAt && (
                                <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>
                                  {moment(product.aiTitleGeneratedAt).format("MMM DD, hh:mm A")}
                                </div>
                              )}
                            </div>
                          ) : product.aiTitleStatus === "failed" || product.aiTitleError ? (
                            <div>
                              <span className="ai-badge failed" style={{ fontSize: "0.65rem" }}>Failed</span>
                              {product.aiTitleGeneratedAt && (
                                <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>
                                  {moment(product.aiTitleGeneratedAt).format("MMM DD, hh:mm A")}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button 
                              className="ai-btn-outline" 
                              style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem", borderColor: "#3b82f6", color: "#3b82f6" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductRowClick(product._id);
                              }}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon fontSize="inherit" style={{ marginRight: "4px" }} /> : <KeyboardArrowDownIcon fontSize="inherit" style={{ marginRight: "4px" }} />}
                              Preview Title 
                            </button>
                            <button 
                              className="ai-btn-outline" 
                              style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewProductId(product._id);
                                setSelectedProductOption({
                                  value: product._id,
                                  label: `${product.title} (Model: ${product.modelNumber || "N/A"})`
                                });
                                setActiveTab("preview");
                              }}
                            >
                              <VisibilityIcon fontSize="inherit" style={{ marginRight: "4px" }} />
                              Audit Tool
                            </button>
                            {product.aiTitle && (
                              <button 
                                className="ai-btn-outline" 
                                style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem", borderColor: "#10b981", color: "#10b981" }}
                                onClick={(e) => handleCopyToClipboard(product.aiTitle, e)}
                              >
                                <ContentCopyIcon fontSize="inherit" style={{ marginRight: "4px" }} />
                                Copy
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ backgroundColor: "#f8fafc" }}>
                          <td colSpan={5} style={{ padding: "1.25rem 1.5rem" }}>
                            <div className="d-flex flex-column gap-3 p-3" style={{ border: "1px solid #e2e8f0", borderRadius: "12px", backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="m-0 font-weight-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: "0.95rem" }}>
                                  <AutoAwesomeIcon style={{ fontSize: "1.1rem", color: "#3b82f6" }} />
                                  Title Optimization Comparison & Analysis
                                </h6>
                                <span 
                                  className="text-muted font-weight-bold" 
                                  onClick={(e) => { e.stopPropagation(); setExpandedProductId(null); }}
                                  style={{ fontSize: "0.75rem", cursor: "pointer" }}
                                >
                                  ✕ Close
                                </span>
                              </div>

                              <div className="row">
                                <div className="col-12 col-md-6 mb-3 mb-md-0">
                                  <div className="p-3" style={{ border: "1px solid #f1f5f9", borderRadius: "8px", backgroundColor: "#f8fafc", height: "100%" }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-muted font-weight-600" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>ORIGINAL TITLE</span>
                                      <span className="badge bg-secondary" style={{ fontSize: "0.65rem", padding: "0.25rem 0.4rem" }}>
                                        {product.title?.length || 0} Chars
                                      </span>
                                    </div>
                                    <div className="text-dark font-weight-500" style={{ fontSize: "0.95rem", lineHeight: "1.5" }}>
                                      {product.title || "N/A"}
                                    </div>
                                  </div>
                                </div>

                                <div className="col-12 col-md-6">
                                  <div className="p-3" style={{ border: "1px solid #dcfce7", borderRadius: "8px", backgroundColor: "#f0fdf4", height: "100%" }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-success font-weight-700" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>AI OPTIMIZED TITLE</span>
                                      <div className="d-flex gap-1 align-items-center">
                                        <span className="badge bg-success" style={{ fontSize: "0.65rem", padding: "0.25rem 0.4rem" }}>
                                          {product.aiTitle?.length || 0} Chars
                                        </span>
                                        {product.title && product.aiTitle && (
                                          <span className="badge bg-primary" style={{ fontSize: "0.65rem", padding: "0.25rem 0.4rem" }}>
                                            {product.aiTitle.length > product.title.length ? `+${product.aiTitle.length - product.title.length}` : product.aiTitle.length - product.title.length} Chars Difference
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {product.aiTitle ? (
                                      <>
                                        <div className="text-success font-weight-700 mb-3" style={{ fontSize: "0.95rem", lineHeight: "1.5" }}>
                                          {product.aiTitle}
                                        </div>
                                        <div className="d-flex gap-2">
                                          <button
                                            className="ai-btn-outline"
                                            style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", borderColor: "#10b981", color: "#10b981", backgroundColor: "white" }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard.writeText(product.aiTitle);
                                              dispatch(showSnackBar("AI Title copied!", "success"));
                                            }}
                                          >
                                            <ContentCopyIcon fontSize="inherit" style={{ marginRight: "4px" }} />
                                            Copy Title
                                          </button>
                                        </div>
                                      </>
                                    ) : product.aiTitleError ? (
                                      <div className="text-danger font-weight-500" style={{ fontSize: "0.85rem" }}>
                                        Failed to optimize: {product.aiTitleError}
                                      </div>
                                    ) : (
                                      <div className="text-muted font-italic" style={{ fontSize: "0.85rem" }}>
                                        No optimization generated yet.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {productsPagination.totalPages > 1 && (
            <div className="d-flex justify-content-center align-center p-3">
              <Pagination
                count={productsPagination.totalPages}
                page={productsPagination.currentPage}
                onChange={(e, val) => setProductsPagination(prev => ({ ...prev, currentPage: val }))}
              />
            </div>
          )}
        </DashboardPagesLayout>
      )}

      {/* Diagnostics Modal */}
      <Dialog 
        open={diagnosticsModalOpen} 
        onClose={() => setDiagnosticsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="font-weight-bold d-flex align-items-center gap-2">
          <InfoIcon style={{ color: "#3b82f6" }} /> 
          AI Provider Health Diagnostics
        </DialogTitle>
        <DialogContent dividers>
          {healthDiagnostics && (
            <div>
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <div className="p-3 bg-light rounded border">
                    <h6 className="font-weight-bold text-muted mb-2">Primary Provider</h6>
                    <div className="text-primary font-weight-bold text-capitalize" style={{ fontSize: "1.1rem" }}>
                      {healthDiagnostics.primaryProvider || "None"}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="p-3 bg-light rounded border">
                    <h6 className="font-weight-bold text-muted mb-2">Preferred Provider</h6>
                    <div className="text-dark font-weight-bold text-capitalize" style={{ fontSize: "1.1rem" }}>
                      {healthDiagnostics.preferredProvider || "None"}
                    </div>
                  </div>
                </div>
              </div>

              <h6 className="font-weight-bold mb-3">Configured Provider Fallback Chain</h6>
              <div className="d-flex flex-wrap gap-2 mb-4">
                {healthDiagnostics.providerChain?.map((p, idx) => (
                  <span key={idx} className="ai-badge completed px-3 py-2 text-capitalize" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
                    {idx + 1}. {p}
                  </span>
                ))}
              </div>

              <h6 className="font-weight-bold mb-3">Provider Status Breakdown</h6>
              <div className="table-responsive">
                <table className="table table-bordered table-hover ai-table">
                  <thead className="bg-light">
                    <tr>
                      <th>Provider</th>
                      <th>Available</th>
                      <th>Enabled</th>
                      <th>Configured</th>
                      <th>Has API Key</th>
                      <th>Reason / Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthDiagnostics.providers?.map((provider, idx) => (
                      <tr key={idx}>
                        <td className="font-weight-bold text-capitalize">{provider.name}</td>
                        <td>
                          {provider.available 
                            ? <span className="text-success d-flex align-items-center gap-1"><CheckCircleIcon fontSize="small" /> Yes</span> 
                            : <span className="text-danger d-flex align-items-center gap-1"><CancelIcon fontSize="small" /> No</span>}
                        </td>
                        <td>{provider.enabled ? "Yes" : "No"}</td>
                        <td>{provider.configured ? "Yes" : "No"}</td>
                        <td>{provider.hasApiKey ? "Yes" : "No"}</td>
                        <td>
                          <span className={provider.available ? "text-success font-weight-bold" : "text-muted"}>
                            {provider.reason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagnosticsModalOpen(false)} style={{ color: "#475569", fontWeight: "bold" }}>
            Close Diagnostics
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AiProductTitles;
